import {
  LIEN_MATCHES_KIND,
  lienMatchesSignature,
  relatedPermitKey,
  type LienMatchesEnrichment,
  type RecordEnrichment,
} from './ai-artifacts';
import { isDistrictWideRecording } from './assessment-reconciliation';
import { fireInspectionNeedsFollowUp, isExplicitlyClosed } from './issues/index';
import {
  groupLiens,
  isPriorOwnerLien,
  lienMatchCandidates,
  validateLienMatches,
  type LienAiConfidence,
  type LienReleaseMatch,
} from './liens';
import { isClosed, sf311CaseSourceUrl } from './presentation';
import { PUBLIC_SOURCE_URLS } from './public-preview';
import { documentTime } from './record-dates';
import { panoramaUrl } from '../skills/us-street-view/scripts/street-view';
import type {
  PropertySummary,
  RecorderDocument,
  RecorderSnapshot,
  RecordKind,
  RecordSummary,
  SourceCoverage,
  SubdivisionActivity,
} from './types';

/** Recorded documents about this parcel specifically. */
export function parcelRecordings(recorder: RecorderSnapshot | null | undefined) {
  return (recorder?.documents ?? []).filter((document) => !isDistrictWideRecording(document));
}

/**
 * Stored model lien pairings for these documents, accepted only when they
 * were generated from exactly the current open liens and unused releases.
 */
export function propertyLienMatches(summary: PropertySummary, documents: RecorderDocument[]) {
  const stored = (summary.enrichments ?? []).find(
    (item): item is LienMatchesEnrichment => item.kind === LIEN_MATCHES_KIND,
  );
  if (!stored) return [];
  const candidates = lienMatchCandidates(documents);
  if (!candidates.length || stored.signature !== lienMatchesSignature(candidates)) return [];
  return validateLienMatches(stored.matches, candidates);
}

export function lienStatus(lien: { release: unknown; match: LienReleaseMatch | null }) {
  if (lien.match === 'ai-inferred') return 'Likely released (AI matched)';
  if (lien.release) return 'Released';
  if (lien.match === 'balanced-batch') return 'Released (batch matched)';
  return 'Release not found';
}

/** Validate attachment URLs separately from city record navigation. */
export function safeExternalUrl(value: string | undefined) {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password)
      return undefined;
    return url.href;
  } catch {
    return undefined;
  }
}

export function safetySourceFreshness(summary: PropertySummary, source: SourceCoverage['source']) {
  const safety = summary.safetyCompliance;
  if (!safety) return undefined;
  switch (source) {
    case 'section604':
      return safety.section604;
    case 'fire-inspections':
      return safety.fireInspections;
    case 'fire-violations':
      return safety.fireViolations;
    case 'fire-compliance':
      return safety.statements;
    default:
      return undefined;
  }
}

export type DisplayRecord = RecordSummary & {
  ai?: RecordEnrichment;
  /** The worker narrates this record but its current explanation is not stored yet. */
  aiPending?: boolean;
  streetViewHeading?: number;
  /** Signed same-origin proxy path stamped server-side. */
  streetViewThumbnail?: string;
  archived: boolean;
  closedAt?: string;
  lien?: {
    released: boolean;
    match: LienReleaseMatch | null;
    batchReleases: Array<{ id: string; date: string; url: string }>;
    documents: Array<{ id: string; date: string; url: string }>;
    confidence?: LienAiConfidence;
    /** Recorded against a previous owner rather than the buyers on the latest sale. */
    priorOwner?: boolean;
  };
  activities?: SubdivisionActivity[];
  attachmentUrl?: string;
  /** Internal Recorder document id used by the authenticated preview route. */
  recorderDocumentId?: string;
  /** When the case was first opened, if the source publishes it separately from `date`. */
  openedAt?: string;
  relatedPermits?: Array<{
    id: string;
    kind: RecordKind;
    status: string;
    date: string;
    archived: boolean;
    sourceUrl?: string;
  }>;
};

export function recordSourceLabel(kind: RecordKind) {
  if (kind === 'complaint') return 'complaintCaseHistory';
  if (kind === 'nov') return 'noticeOfViolation';
  if (kind === 'street-view') return 'source';
  return 'openSource';
}

/**
 * Every record on the property page, without AI explanations attached; see
 * `propertyRecords` in property-graph for the enriched list.
 */
export function displayRecords(summary: PropertySummary): DisplayRecord[] {
  const records: DisplayRecord[] = [
    ...(summary.open ?? []).map((record) => ({ ...record, archived: false })),
    ...(summary.historicalPermits ?? []).map((record) => ({
      ...record,
      archived: true,
    })),
    ...(summary.recentlyClosed ?? []).map((record) => ({
      ...record,
      archived: true,
    })),
    ...(summary.olderClosed ?? []).map((record) => ({
      ...record,
      archived: true,
    })),
  ];
  const add = (
    record: RecordSummary,
    fields: Record<string, string>,
    archived = isClosed(record.status),
    details: Pick<
      DisplayRecord,
      | 'activities'
      | 'attachmentUrl'
      | 'openedAt'
      | 'closedAt'
      | 'lien'
      | 'recorderDocumentId'
      | 'streetViewHeading'
      | 'streetViewThumbnail'
    > = {},
  ) => {
    records.push({
      ...record,
      archived,
      ...details,
      pages: [{ title: '', fields, tables: [], links: [] }],
    });
  };
  for (const record of summary.publicWorks?.records ?? []) {
    records.push({
      ...record,
      archived: isClosed(record.status),
      relatedSources: record.pages.flatMap((page) => page.links),
    });
  }
  summary.sf311?.cases.forEach((item) =>
    add(
      {
        id: item.id,
        kind: '311',
        status: item.status,
        date: item.updatedAt || item.openedAt,
        address: item.address,
        description: [item.category, item.details].filter(Boolean).join(' · '),
        sourceUrl: sf311CaseSourceUrl(item.id),
      },
      {
        ...item.fields,
        ...(item.category && !item.fields.Category ? { Category: item.category } : {}),
        ...(item.subtype && !item.fields['Request type'] ? { 'Request type': item.subtype } : {}),
        ...(item.description ? { Description: item.description } : {}),
        ...item.attributes,
      },
      Boolean(item.closedAt) || isClosed(item.status),
      {
        attachmentUrl: safeExternalUrl(item.mediaUrl),
        openedAt: item.openedAt,
        closedAt: item.closedAt || undefined,
      },
    ),
  );
  summary.subdivision?.projects.forEach((item) =>
    add(
      {
        id: item.id,
        kind: 'subdivision',
        status: item.status,
        date: item.updatedAt,
        address: item.addresses.join(', '),
        description: item.name || item.type,
        sourceUrl: item.sourceUrl,
      },
      item.fields,
      isClosed(item.status),
      { activities: item.activities },
    ),
  );
  summary.safetyCompliance?.fireViolations.violations.forEach((item) =>
    add(
      {
        id: item.id,
        kind: 'fire-violation',
        status: item.status,
        date: item.violationDate,
        address: item.address,
        description: item.description,
        sourceUrl: item.sourceUrl,
      },
      item.fields,
      isExplicitlyClosed(item.status, item.closeDate, 'fire-violations', 'fire-violation'),
      { closedAt: item.closeDate || undefined },
    ),
  );
  summary.safetyCompliance?.fireInspections.inspections.forEach((item) =>
    add(
      {
        id: item.id,
        kind: 'fire-inspection',
        status: item.status,
        date: item.startedAt,
        address: item.address,
        description: item.description || item.type,
        sourceUrl: item.sourceUrl,
      },
      item.fields,
      isExplicitlyClosed(item.status, item.endedAt, 'fire-inspections', 'fire-inspection'),
      { closedAt: item.endedAt || undefined },
    ),
  );
  summary.safetyCompliance?.section604.affidavits.forEach((item) =>
    add(
      {
        id: item.id,
        kind: 'section604',
        status: '',
        date: item.inspectionDate,
        address: item.address,
        description: item.comments,
        sourceUrl: item.sourceUrl,
      },
      item.fields,
      true,
    ),
  );
  summary.safetyCompliance?.statements.statements.forEach((item) =>
    add(
      {
        id: item.id,
        kind: 'fire-compliance',
        status: '',
        date: item.date,
        address: item.address,
        description: '',
        sourceUrl: item.documentUrl || item.sourceUrl,
      },
      item.fields,
      true,
    ),
  );
  const recordings = parcelRecordings(summary.recorder);
  const liens = groupLiens(recordings, propertyLienMatches(summary, recordings));
  const releases = new Set(liens.flatMap((l) => (l.release ? [l.release.id] : [])));
  const byId = new Map(liens.map((l) => [l.document.id, l]));
  recordings
    .filter((item) => !releases.has(item.id))
    .forEach((item) =>
      add(
        {
          id: item.documentNumber || item.id,
          kind: 'recorder',
          status: byId.has(item.id) ? lienStatus(byId.get(item.id)!) : '',
          date: byId.get(item.id)?.release?.documentDate ?? item.documentDate,
          address: summary.displayAddress,
          description: item.titles.join(', '),
          sourceUrl: item.sourceUrl,
        },
        {
          ...item.fields,
          documentNumber: item.documentNumber,
          ...(item.names?.length ? { Names: item.names.join(' · ') } : {}),
        },
        byId.has(item.id)
          ? Boolean(byId.get(item.id)!.release || byId.get(item.id)!.match === 'balanced-batch')
          : true,
        {
          recorderDocumentId: item.id,
          ...(byId.has(item.id)
            ? {
                closedAt: byId.get(item.id)!.release?.documentDate,
                lien: {
                  released: Boolean(
                    byId.get(item.id)!.release || byId.get(item.id)!.match === 'balanced-batch',
                  ),
                  match: byId.get(item.id)!.match,
                  ...(byId.get(item.id)!.confidence
                    ? { confidence: byId.get(item.id)!.confidence }
                    : {}),
                  ...(isPriorOwnerLien(item, recordings) ? { priorOwner: true } : {}),
                  batchReleases: byId.get(item.id)!.batchReleases.map((document) => ({
                    id: document.documentNumber,
                    date: document.documentDate,
                    url: document.sourceUrl,
                  })),
                  documents: [
                    item,
                    ...(byId.get(item.id)!.release ? [byId.get(item.id)!.release!] : []),
                  ].map((d) => ({
                    id: d.documentNumber,
                    date: d.documentDate,
                    url: d.sourceUrl,
                  })),
                },
              }
            : {}),
        },
      ),
    );
  for (const capture of summary.streetView ?? [])
    add(
      {
        id: capture.id,
        kind: 'street-view',
        status: '',
        date: capture.date,
        address: summary.displayAddress,
        description: 'Street View capture',
        sourceUrl: panoramaUrl(capture.id),
      },
      {},
      true,
      { streetViewHeading: capture.heading, streetViewThumbnail: capture.thumbnail },
    );
  const permitGroups = new Map<string, DisplayRecord[]>();
  for (const record of records) {
    const key = relatedPermitKey(record.kind, record.description ?? '');
    if (!key) continue;
    const group = permitGroups.get(key) ?? [];
    group.push(record);
    permitGroups.set(key, group);
  }
  for (const record of records) {
    const key = relatedPermitKey(record.kind, record.description ?? '');
    const related = (permitGroups.get(key) ?? []).filter((candidate) => candidate.id !== record.id);
    if (related.length)
      record.relatedPermits = related.map((candidate) => ({
        id: candidate.id,
        kind: candidate.kind,
        status: candidate.status,
        date: candidate.date,
        archived: candidate.archived,
        sourceUrl: candidate.sourceUrl,
      }));
    if (record.archived && !record.closedAt && ['nov', 'complaint'].includes(record.kind)) {
      record.closedAt = dbiClosedAt(record);
    }
  }
  return records.sort(
    (a, b) =>
      (documentTime(b.activity?.lastActivityAt ?? b.date) ?? 0) -
        (documentTime(a.activity?.lastActivityAt ?? a.date) ?? 0) ||
      (a.sourceOrder ?? Number.MAX_SAFE_INTEGER) - (b.sourceOrder ?? Number.MAX_SAFE_INTEGER),
  );
}

export function isDisplayCoverageComplete(summary: PropertySummary) {
  const runtimeCoverage = (summary.runtime?.coverage ?? []).filter(
    (source) => source.status !== 'not-applicable',
  );
  if (runtimeCoverage.some((source) => source.status !== 'complete')) return false;
  const sources = summary.health.sources ?? [];
  if (
    summary.health.complete &&
    (sources.length > 0 || runtimeCoverage.length > 0) &&
    sources.every((source) => source.status === 'complete')
  )
    return true;
  const preview = summary.preview;
  return Boolean(
    preview?.phase === 'complete' &&
    preview.novsComplete &&
    Object.values(preview.recordDetails).every((status) => status === 'complete') &&
    (Object.keys(PUBLIC_SOURCE_URLS) as SourceCoverage['source'][]).every((source) => {
      const status = preview.sourceStates[source]?.status;
      return status === 'complete' || (source === 'subdivision' && status === 'not-checked');
    }),
  );
}

/** Historical placement describes recorded activity, never a new city status. */
export function permitHistoryReason(record: DisplayRecord) {
  if (!record.archived || !isPermit(record.kind) || isClosed(record.status)) return null;
  const reason = record.activity?.reason;
  return reason === 'no-recent-activity' || reason === 'activity-date-unavailable' ? reason : null;
}

export function needsAttention(record: DisplayRecord) {
  if (record.archived) return false;
  // An inspection the fire department says still needs a follow-up visit.
  if (record.kind === 'fire-inspection') return fireInspectionNeedsFollowUp(record.status);
  return ['complaint', 'nov', 'fire-violation'].includes(record.kind);
}

/** A notice resolved through its complaint's case history while DBI still lists it as open. */
export function resolvedDespiteCityStatus(record: DisplayRecord) {
  return (
    record.kind === 'nov' && record.archived && Boolean(record.status) && !isClosed(record.status)
  );
}

export function isPermit(kind: RecordKind) {
  return ['building', 'electrical', 'plumbing'].includes(kind);
}

/**
 * SF311 categories whose open reports can become owner-facing enforcement —
 * building/housing complaints DBI or Housing may turn into an NOV, graffiti on
 * private structures the owner must abate, owner-maintained sidewalk defects,
 * shared-spaces (parklet) violations, and construction noise. Everything else
 * is a request about the city itself: informational on the timeline, never an
 * action item.
 */
const SF311_OWNER_FACING: { category: RegExp; subtype?: RegExp }[] = [
  { category: /^residential building( request)?$/i },
  {
    category: /^general request - building inspection$/i,
    subtype: /complaint/i,
  },
  { category: /^shared spaces violation$/i },
  { category: /^graffiti private$/i },
  { category: /^graffiti$/i, subtype: /building|sidewalk/i },
  {
    category: /^(sidewalk or curb|sidewalk and curb|street defects?)$/i,
    subtype: /sidewalk|sewer vent/i,
  },
  {
    category: /^noise( report)?$/i,
    subtype: /construction|home improvement|mechanical equipment/i,
  },
];

export function sf311OwnerFacing(record: DisplayRecord) {
  const fields = record.pages?.[0]?.fields ?? {};
  const category = fields.Category ?? '';
  const subtype = fields['Request type'] ?? '';
  return SF311_OWNER_FACING.some(
    (rule) => rule.category.test(category.trim()) && (!rule.subtype || rule.subtype.test(subtype)),
  );
}

/** Only explicit city closure fields/events establish when a case was closed. */
function dbiClosedAt(record: RecordSummary) {
  const dates: string[] = [];
  for (const page of record.pages ?? []) {
    for (const [key, value] of Object.entries(page.fields)) {
      if (
        /^(closed|closure|resolved|complied|completed|abated)(\s+date)?$|^date\s+(closed|resolved|complied|completed|abated)$/i.test(
          key.trim(),
        ) &&
        !Number.isNaN(Date.parse(value))
      )
        dates.push(value);
    }
    for (const table of page.tables) {
      const headers = table.headers.map((header) => header.toLowerCase().trim());
      const date = headers.indexOf('date');
      const status = headers.indexOf('status');
      if (date < 0 || status < 0) continue;
      for (const row of table.rows) {
        if (
          /^(closed|resolved|complied|completed|abated)$/i.test(row[status]?.trim() ?? '') &&
          row[date] &&
          !Number.isNaN(Date.parse(row[date]!))
        )
          dates.push(row[date]!);
      }
    }
  }
  return dates.sort((a, b) => (documentTime(b) ?? 0) - (documentTime(a) ?? 0))[0];
}
